import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { EmotionLevel } from './EmotionSelector';
import { Stage, ContentItem } from '@/types/journey';
import StageHeader from './StageHeader';
import StageContent from './StageContent';
import StageActions from './StageActions';
import QuoteBubble from './QuoteBubble';
import EmotionFace from './EmotionFace';
import EmotionSelector from './EmotionSelector';

interface JourneyStageProps {
  stage: Stage;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onEmotionChange: (value: EmotionLevel) => void;
  onQuoteChange: (quote: string) => void;
  onTitleChange?: (title: string) => void;
  onDescriptionChange?: (description: string) => void;
  onPainPointsChange?: (painPoints: string) => void;
  onOpportunitiesChange?: (opportunities: string) => void;
  onContentUpdate?: (contentItems: ContentItem[]) => void;
  stageHeight?: number; // New prop for consistent height
}

const JourneyStage: React.FC<JourneyStageProps> = ({ 
  stage, 
  index, 
  onEdit, 
  onDelete,
  onEmotionChange,
  onQuoteChange,
  onTitleChange,
  onDescriptionChange,
  onPainPointsChange,
  onOpportunitiesChange,
  onContentUpdate,
  stageHeight // Get the height prop
}) => {
  // Check if all important fields are filled
  const isComplete = React.useMemo(() => {
    const hasTitle = !!stage.title && stage.title !== `Stage ${index + 1}`;
    
    // Either old fields are filled or we have new content items
    const hasLegacyFields = !!stage.painPoints && !!stage.opportunities; 
    const hasContentItems = stage.contentItems && stage.contentItems.length > 0;
    
    const hasQuote = !!stage.quote;
    
    return hasTitle && (hasLegacyFields || hasContentItems) && hasQuote;
  }, [stage, index]);

  // Handle content updates
  const handleContentUpdate = (contentItems: ContentItem[]) => {
    if (onContentUpdate) {
      onContentUpdate(contentItems);
    }
  };

  return (
    <div className="relative group">
      <div className="stage-handle left-0 cursor-ew-resize" />
      <div className="stage-handle right-0 cursor-ew-resize" />
      
      <Card 
        className="stage-card w-[336px] animate-slide-in flex flex-col hover:cursor-pointer"
        style={{ 
          height: stageHeight ? `${stageHeight}px` : 'auto',
          overflow: 'hidden'
        }}
      >
        <CardHeader className="p-3 pb-0">
          <StageHeader 
            title={stage.title} 
            index={index} 
            emotion={stage.emotion as EmotionLevel} 
            onTitleChange={onTitleChange}
          />
        </CardHeader>
        
        <CardContent className="p-3 space-y-2.5 overflow-y-auto flex-1">
          <StageContent 
            description={stage.description} 
            painPoints={stage.painPoints} 
            opportunities={stage.opportunities}
            contentItems={stage.contentItems || []}
            onDescriptionChange={onDescriptionChange}
            onPainPointsChange={onPainPointsChange}
            onOpportunitiesChange={onOpportunitiesChange}
            onContentUpdate={handleContentUpdate}
          />
        </CardContent>
        
        <CardFooter className="p-3 pt-0 flex flex-col gap-3 border-t mt-auto bg-white dark:bg-gray-800">
          <div className="grid grid-cols-5 gap-2 w-full pt-2">
            <div className="col-span-3 z-10">
              <QuoteBubble 
                quote={stage.quote || ''} 
                emotion={stage.emotion as EmotionLevel}
                onQuoteChange={onQuoteChange}
              />
            </div>
            
            <div className="col-span-1 z-10">
              <EmotionFace emotion={stage.emotion as EmotionLevel} />
            </div>
            
            <div className="col-span-1 z-10">
              <EmotionSelector 
                value={stage.emotion as EmotionLevel} 
                onChange={onEmotionChange}
                className="scale-75 origin-center" 
              />
            </div>
          </div>
          
          <StageActions onEdit={onEdit} onDelete={onDelete} isComplete={isComplete} />
        </CardFooter>
      </Card>
    </div>
  );
};

export default JourneyStage;
